import React from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, Clock, Check } from 'lucide-react'
import ScrollReveal from '../components/ScrollReveal'
import { updateMeta } from '../lib/utils'

export default function AboutPage() {
  React.useEffect(() => {
    updateMeta(
      "Om os | Mød din revisor | Thellefsen Regnskab",
      "Mød Peter Thellefsen, din personlige revisor. Mange års erfaring med tryg rådgivning og regnskab for mindre selskaber og selvstændige."
    )
  }, [])

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Header Section */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight">
            Om Thellefsen Regnskab ApS
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Speciale i mindre selskaber og enkeltmandsvirksomheder
          </p>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Image Column */}
            <div className="md:col-span-6 lg:col-span-5 flex justify-center">
              <div className="relative w-full max-w-md">
                <img
                  src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/bogfoering-og-aarsregnskab-arbejdsbord.jpg"
                  alt="Økonomisk overblik med regnskabsark, lommeregner og pen hos Thellefsen Regnskab"
                  className="rounded-none w-full object-cover aspect-[4/5]"
                />
              </div>
            </div>

            {/* Content Column */}
            <div className="md:col-span-6 lg:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Professionel regnskabshjælp i øjenhøjde
              </h2>

              <p className="text-stone-600 leading-relaxed">
                Hos{' '}
                <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
                  Thellefsen Regnskab
                </Link>{' '}
                har vi mere end 10 års erfaring inden for regnskab og rådgivning til mindre virksomheder. Erfaringen omfatter blandt andet arbejde med mindre selskaber og enkeltmandsvirksomheder, hvor praktisk og forståelig revisorhjælp er i fokus. Der tages kunder ind fra hele Danmark.
              </p>

              <p className="text-stone-600 leading-relaxed">
                Virksomheden drives med et bevidst fokus på personlig service. Det betyder, at du altid har direkte kontakt til den samme revisor, som kender din virksomhed og prioriterer løbende dialog samt hurtige svar på spørgsmål gennem året.
              </p>

              <p className="text-stone-600 leading-relaxed">
                Hovedformålet er at tilbyde en samlet regnskabsløsning, hvor hele regnskabet håndteres til en fast og konkurrencedygtig pris. Samtidig lægges der stor vægt på at gøre regnskabsprocessen så enkel, overskuelig og tidsbesparende som muligt for dig som virksomhedsejer.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-stone-900 shrink-0" />
                  <span className="text-sm font-semibold text-stone-900">100% personlig kontakt</span>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-stone-900 shrink-0" />
                  <span className="text-sm font-semibold text-stone-900">Hurtig respons</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fordele ved at vælge os Section */}
      <section className="py-20 bg-white border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Fordele ved at vælge os
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            <ScrollReveal direction="left">
              <div className="bg-white border border-stone-200 p-8 h-full flex flex-col justify-between shadow-md">
                <div className="space-y-4">
                  <div className="text-stone-900 flex items-center justify-start">
                    <Check className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900">Komplet regnskabsløsning</h3>
                  <p className="text-stone-600 leading-relaxed text-sm">
                    Når du vælger os, får du en samlet regnskabsløsning til en fast årlig pris på 8.000 kr. ekskl. moms. Aftalen dækker både løbende bogføring, momsindberetning, årsregnskab og selvangivelse – og du kan altid stille spørgsmål undervejs uden ekstra omkostninger.
                  </p>
                  <Link to="/regnskabsservice" className="inline-block pt-2 text-xs font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2">
                    Læs mere om samlet regnskabsservice &rarr;
                  </Link>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right">
              <div className="bg-white border border-stone-200 p-8 h-full flex flex-col justify-between shadow-md">
                <div className="space-y-4">
                  <div className="text-stone-900 flex items-center justify-start">
                    <Check className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900">Udarbejdelse af årsregnskab</h3>
                  <p className="text-stone-600 leading-relaxed text-sm">
                    Udarbejdelse af årsregnskab samt indberetning kan tilbydes til en fast pris på 4.000 kr. ekskl. moms. Du har fuldt overblik og slipper for det administrative arbejde.
                  </p>
                  <Link to="/aarsrapport-og-aarsregnskab" className="inline-block pt-2 text-xs font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2">
                    Læs mere om årsrapport & årsregnskab &rarr;
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Ingen ekstra udgifter til regnskabsprogram */}
          <ScrollReveal direction="up" delay={100}>
            <div className="mt-12 bg-orange-50 border border-orange-200 p-8 text-center max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-stone-900 mb-3">Ingen ekstra udgifter til regnskabsprogram​</h3>
              <p className="text-stone-600 leading-relaxed text-sm max-w-2xl mx-auto">
                Vores priser inkluderer et professionelt regnskabsprogram, som bruges til den daglige bogføring. Det betyder, at du ikke skal betale separat for et regnskabssystem og typisk kan spare op mod 4.000 kr. om året i softwareudgifter.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-stone-100 border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Hvad du kan forvente af samarbejdet
            </p>
            <p className="text-stone-600 leading-relaxed">
              Vi bygger vores forretning på tillid, gennemskuelighed og personlig relation. Det er din garanti for et trygt samarbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Value 1 */}
            <div className="bg-white rounded-none border border-stone-200 p-8 shadow-sm md:col-span-6 lg:col-span-4">
              <h3 className="text-xl font-bold text-stone-900 mb-3">1. Gennemskuelige priser</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Ingen ubehagelige overraskelser eller uforudsete regninger. Du får altid en fast, skriftlig pris, inden vi går i gang med din opgave.
              </p>
            </div>

            {/* Value 2 */}
            <div className="bg-white rounded-none border border-stone-200 p-8 shadow-sm md:col-span-6 lg:col-span-4">
              <h3 className="text-xl font-bold text-stone-900 mb-3">2. Direkte kontakt</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Ingen telefonsluser eller uerfarne assistenter. Du har direkte adgang til din revisor på telefon og e-mail, når du har brug for det.
              </p>
            </div>

            {/* Value 3 */}
            <div className="bg-white rounded-none border border-stone-200 p-8 shadow-sm md:col-span-12 lg:col-span-4">
              <h3 className="text-xl font-bold text-stone-900 mb-3">3. Ro i maven</h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Vi sørger for, at alle frister, regler og indberetninger overholdes til punkt og prikke, så du kan fokusere 100% på din forretning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white text-center border-b border-stone-200">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-2xl sm:text-3xl font-serif text-stone-900 font-bold">
            Vil du have en uforpligtende snak om dit regnskab?
          </h2>
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto">
            Kontakt os i dag og hør, hvordan vi kan gøre din økonomi mere overskuelig til en fast og gennemskuelig pris.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-none bg-orange-600 px-8 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-orange-700 transition-all duration-200"
            >
              Kontakt os i dag
            </Link>
            <Link
              to="/ydelser"
              className="inline-flex items-center justify-center rounded-none bg-white border border-stone-300 px-7 py-3.5 text-sm font-semibold text-stone-800 hover:bg-stone-50 transition-all duration-200"
            >
              Se alle ydelser
            </Link>
          </div>
        </div>
      </section>

    </div>
  )
}
